import React, { useState, useEffect, useCallback } from 'react';
import { MessageContext } from './MessageContext';
import type { MessageContextType } from '@/types/message';
import { Message, Conversation, MessageState } from '@/types/message';
import { useAuth } from '@/hooks/use-auth';
import { useSupabase } from '@/contexts/supabase/SupabaseClientProvider';
import { addBreadcrumb } from '@/lib/sentry';

export function MessageProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const supabase = useSupabase();
  const isUuid = (v: string) => /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.test(v);
  const [messageState, setMessageState] = useState<MessageState>({
    conversations: [],
    messages: {},
    activeConversationId: null,
    isLoading: true,
    moreAvailable: {},
  });

  const getSenderInfo = useCallback(
    async (userId: string): Promise<{ name: string; avatarUrl?: string }> => {
      const { data, error } = await supabase
        .from('profiles')
        .select('first_name,last_name,avatar_url')
        .eq('user_id', userId)
        .maybeSingle();
      if (error || !data) return { name: 'Unknown' };
      const parts = [data.first_name, data.last_name].filter(Boolean);
      return { 
        name: parts.join(' ').trim() || 'Unknown',
        avatarUrl: data.avatar_url || undefined
      };
    },
    [supabase]
  );

  // Track real Supabase auth session presence to support "offline/mock" mode
  const offlineMode = !user || !isUuid(user.id);

  const loadData = useCallback(async () => {
    if (offlineMode) {
      setMessageState(prev => ({ ...prev, isLoading: false }));
      return;
    }
    setMessageState(prev => ({ ...prev, isLoading: true }));
    try {
      const { data: participantRows, error } = await supabase
        .from('conversation_participants')
        .select('conversation_id,last_read_at')
        .eq('user_id', user.id);
      if (error) throw error;

      const conversations: Conversation[] = [];
      const messagesByConv: Record<string, Message[]> = {};
      const moreByConv: Record<string, boolean> = {};
      const convIds = participantRows.map(r => r.conversation_id);

      // Fetch conversations separately to avoid recursion in RLS policies
      const { data: convRows } = convIds.length
        ? await supabase
            .from('conversations')
            .select('*')
            .in('id', convIds)
        : { data: [] as any[] } as any;
      const convMap = new Map<string, any>();
      (convRows || []).forEach((c: any) => convMap.set(c.id, c));

      // Fetch all participants for these conversations (avoid nested recursion)
      const { data: allParticipants } = convIds.length
        ? await supabase
            .from('conversation_participants')
            .select('conversation_id,user_id')
            .in('conversation_id', convIds)
        : { data: [] as any[] } as any;
      const participantsByConv = new Map<string, string[]>();
      (allParticipants || []).forEach((row: any) => {
        const arr = participantsByConv.get(row.conversation_id) || [];
        arr.push(row.user_id);
        participantsByConv.set(row.conversation_id, arr);
      });

      // Short-term: fetch last message per conversation for previews
      // NOTE: This loops per conversation; acceptable for modest counts. Consider RPC for scale.
      const lastMessageMap = new Map<string, any | null>();
      await Promise.all(
        convIds.map(async (id) => {
          const { data: lastRow } = await supabase
            .from('messages')
            .select('id,conversation_id,sender_id,content,created_at')
            .eq('conversation_id', id)
            .order('created_at', { ascending: false })
            .limit(1);
          lastMessageMap.set(id, lastRow && lastRow[0] ? lastRow[0] : null);
        })
      );
      // Collect both last message senders AND all conversation participants for avatars
      const senderIds = Array.from(new Set(Array.from(lastMessageMap.values()).filter(Boolean).map((m: any) => m.sender_id)));
      const allParticipantIds = Array.from(new Set(Array.from(participantsByConv.values()).flat()));
      const allUserIds = Array.from(new Set([...senderIds, ...allParticipantIds]));
      
      const senderMap = new Map<string, { name: string; avatarUrl?: string }>();
      if (allUserIds.length) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id,first_name,last_name,avatar_url')
          .in('user_id', allUserIds);
        (profiles || []).forEach(p => {
          const parts = [p.first_name, p.last_name].filter(Boolean);
          senderMap.set(p.user_id, {
            name: parts.join(' ').trim() || 'Unknown',
            avatarUrl: p.avatar_url || undefined
          });
        });
      }

      participantRows.forEach(row => {
        const convData = convMap.get(row.conversation_id);
        const lastReadAt = row.last_read_at ? new Date(row.last_read_at) : null;
        const lastRaw = lastMessageMap.get(row.conversation_id);
        const senderInfo = senderMap.get(lastRaw?.sender_id);
        const lastMessage: Message | undefined = lastRaw
          ? {
              id: lastRaw.id,
              senderId: lastRaw.sender_id,
              senderName: senderInfo?.name || 'Unknown',
              senderAvatarUrl: senderInfo?.avatarUrl,
              content: lastRaw.content,
              timestamp: new Date(lastRaw.created_at),
              type: convData.type,
              conversationId: lastRaw.conversation_id,
              isRead: lastReadAt ? new Date(lastRaw.created_at) <= lastReadAt || lastRaw.sender_id === user.id : lastRaw.sender_id === user.id,
            }
          : undefined;
        // unread count unknown until conversation is opened; default to 0 here
        const unreadCount = lastMessage && !lastMessage.isRead && lastMessage.senderId !== user.id ? 1 : 0;

        // For direct conversations, get the other participant's avatar
        const otherParticipantId = convData.type === 'direct' 
          ? (participantsByConv.get(convData.id) || []).find((id: string) => id !== user.id)
          : null;
        const otherParticipantInfo = otherParticipantId ? senderMap.get(otherParticipantId) : null;

        conversations.push({
          id: convData.id,
          type: convData.type,
          name: convData.name || '',
          participants: participantsByConv.get(convData.id) || [user.id],
          groupId: convData.group_id || undefined,
          subgroupId: convData.subgroup_id || undefined,
          lastMessage,
          unreadCount,
          avatar:
            convData.avatar ||
            (convData.name ? convData.name.split(' ').map((n: string) => n[0]).join('').toUpperCase() : ''),
          avatarUrl: convData.type === 'direct' ? otherParticipantInfo?.avatarUrl : undefined,
          createdAt: new Date(convData.created_at),
          lastReadAt: lastReadAt || undefined,
        });

        messagesByConv[convData.id] = [];
        // Assume history exists; refined on initial fetch of that conversation
        moreByConv[convData.id] = true;
      });

      setMessageState({
        conversations,
        messages: messagesByConv,
        activeConversationId: null,
        isLoading: false,
        moreAvailable: moreByConv,
      });
    } catch (err) {
      console.error('Error loading messages:', err);
      setMessageState(prev => ({ ...prev, isLoading: false }));
    }
  }, [offlineMode, supabase, user]);

  const fetchRecentMessages = useCallback(async (conversationId: string, before?: Date) => {
    const limit = 50;
    let q = supabase
      .from('messages')
      .select('id,conversation_id,sender_id,content,created_at')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (before) q = q.lt('created_at', before.toISOString());
    const { data, error } = await q;
    if (error) {
      console.error('Error fetching recent messages:', error);
      return { count: 0 };
    }
    const rows = data || [];
    const senderIds = Array.from(new Set(rows.map(r => r.sender_id)));
    const senderMap = new Map<string, { name: string; avatarUrl?: string }>();
    if (senderIds.length) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id,first_name,last_name,avatar_url')
        .in('user_id', senderIds);
      (profiles || []).forEach(p => {
        const parts = [p.first_name, p.last_name].filter(Boolean);
        senderMap.set(p.user_id, {
          name: parts.join(' ').trim() || 'Unknown',
          avatarUrl: p.avatar_url || undefined
        });
      });
    }
    const batch = rows.slice().reverse().map((m: any) => {
      const senderInfo = senderMap.get(m.sender_id);
      return {
        id: m.id,
        senderId: m.sender_id,
        senderName: senderInfo?.name || 'Unknown',
        senderAvatarUrl: senderInfo?.avatarUrl,
        content: m.content,
        timestamp: new Date(m.created_at),
        type: (messageState.conversations.find(c => c.id === conversationId)?.type) || 'direct',
        conversationId,
        isRead: true,
      } as Message;
    });
    setMessageState(prev => ({
      ...prev,
      messages: { ...prev.messages, [conversationId]: [...(prev.messages[conversationId] || []), ...batch] },
      moreAvailable: { ...(prev.moreAvailable || {}), [conversationId]: rows.length === limit },
    }));
    return { count: batch.length };
  }, [messageState.conversations, supabase]);

  useEffect(() => {
    if (!offlineMode) loadData();
    else setMessageState(prev => ({ ...prev, isLoading: false }));
  }, [offlineMode, loadData]);

  useEffect(() => {
    if (!user || offlineMode) return;
    const channel = supabase
      .channel('public:messages')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, async payload => {
        const msg = payload.new as any;

        const senderInfo = msg.sender_id === user.id 
          ? { name: user.firstName, avatarUrl: user.avatarUrl }
          : await getSenderInfo(msg.sender_id);

        setMessageState(prev => {
          const conv = prev.conversations.find(c => c.id === msg.conversation_id);
          if (!conv) return prev;

          // Deduplicate if already present
          if ((prev.messages[msg.conversation_id] || []).some(m => m.id === msg.id)) {
            return prev;
          }

          const newMessage: Message = {
            id: msg.id,
            senderId: msg.sender_id,
            senderName: senderInfo.name,
            senderAvatarUrl: senderInfo.avatarUrl,
            content: msg.content,
            timestamp: new Date(msg.created_at),
            type: conv.type,
            conversationId: msg.conversation_id,
            isRead: msg.sender_id === user.id,
          };

          const updatedMessages = {
            ...prev.messages,
            [msg.conversation_id]: [...(prev.messages[msg.conversation_id] || []), newMessage],
          };

          const updatedConversations = prev.conversations.map(c =>
            c.id === msg.conversation_id
              ? {
                  ...c,
                  lastMessage: newMessage,
                  unreadCount:
                    msg.sender_id === user.id || prev.activeConversationId === msg.conversation_id
                      ? c.unreadCount
                      : c.unreadCount + 1,
                }
              : c
          );

          return { ...prev, messages: updatedMessages, conversations: updatedConversations };
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [getSenderInfo, offlineMode, supabase, user]);

  const sendMessage = useCallback(
    async (conversationId: string, content: string) => {
      if (!user || !content.trim()) return;

      if (offlineMode) {
        const tempId = 'local-' + Math.random().toString(36).slice(2);
        const optimistic: Message = {
          id: tempId,
          senderId: user.id,
          senderName: user.firstName,
          content: content.trim(),
          timestamp: new Date(),
          type: messageState.conversations.find(c => c.id === conversationId)?.type || 'direct',
          conversationId,
          isRead: true,
          isAdmin: user.isAdmin,
        };
        setMessageState(prev => {
          const updatedMessages = {
            ...prev.messages,
            [conversationId]: [...(prev.messages[conversationId] || []), optimistic],
          };
          const updatedConversations = prev.conversations.map(conv =>
            conv.id === conversationId ? { ...conv, lastMessage: optimistic, unreadCount: 0 } : conv
          );
          return { ...prev, messages: updatedMessages, conversations: updatedConversations };
        });
        return;
      }

      addBreadcrumb('messages', 'sendMessage', { conversationId });

      // Create optimistic message
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const optimistic: Message = {
        id: tempId,
        senderId: user.id,
        senderName: user.firstName,
        content: content.trim(),
        timestamp: new Date(),
        type: messageState.conversations.find(c => c.id === conversationId)?.type || 'direct',
        conversationId,
        isRead: true,
        isAdmin: user.isAdmin,
        pending: true,
      };

      setMessageState(prev => {
        const updatedMessages = {
          ...prev.messages,
          [conversationId]: [...(prev.messages[conversationId] || []), optimistic],
        };
        const updatedConversations = prev.conversations.map(conv =>
          conv.id === conversationId ? { ...conv, lastMessage: optimistic, unreadCount: 0 } : conv
        );
        return { ...prev, messages: updatedMessages, conversations: updatedConversations };
      });

      // Attempt send with one retry
      const attempt = async () => {
        const { data, error } = await supabase
          .from('messages')
          .insert({
            conversation_id: conversationId,
            sender_id: user.id,
            content: content.trim(),
            message_type: 'text',
          })
          .select('id,conversation_id,sender_id,content,created_at')
          .single();
        return { data, error };
      };

      let { data, error } = await attempt();
      if (error || !data) {
        // retry once after short delay
        await new Promise(r => setTimeout(r, 500));
        ({ data, error } = await attempt());
      }

      if (error || !data) {
        console.error('Error sending message:', error);
        setMessageState(prev => ({
          ...prev,
          messages: {
            ...prev.messages,
            [conversationId]: (prev.messages[conversationId] || []).map(m =>
              m.id === tempId ? { ...m, pending: false, failed: true } : m
            ),
          },
        }));
        return;
      }

      // Replace optimistic with real message
      setMessageState(prev => {
        const confirmed: Message = {
          id: data.id,
          senderId: data.sender_id,
          senderName: user.firstName,
          content: data.content,
          timestamp: new Date(data.created_at),
          type: optimistic.type,
          conversationId,
          isRead: true,
          isAdmin: user.isAdmin,
        };
        const updatedMsgs = (prev.messages[conversationId] || []).map(m => (m.id === tempId ? confirmed : m));
        const updatedConversations = prev.conversations.map(conv =>
          conv.id === conversationId ? { ...conv, lastMessage: confirmed, unreadCount: 0 } : conv
        );
        return {
          ...prev,
          messages: { ...prev.messages, [conversationId]: updatedMsgs },
          conversations: updatedConversations,
        };
      });
    },
    [messageState.conversations, offlineMode, supabase, user]
  );

  const sendAdminMessage = useCallback(
    (groupIds: string[], subgroupIds: string[], content: string) => {
      if (!user?.isAdmin || !content.trim()) return;

      const targetConversations = messageState.conversations.filter(
        conv =>
          (conv.groupId && groupIds.includes(conv.groupId)) ||
          (conv.subgroupId && subgroupIds.includes(conv.subgroupId))
      );

      targetConversations.forEach(conv => {
        sendMessage(conv.id, content);
      });
    },
    [user, messageState.conversations, sendMessage]
  );

  const createDirectConversation = useCallback(
    async (participantId: string, participantName: string) => {
      if (!user) return '';

      if (offlineMode) {
        // Create a deterministic local conversation id using pair_key
        const a = user.id;
        const b = participantId;
        const pair = [a, b].sort().join(':');
        const convId = `local-${btoa(unescape(encodeURIComponent(pair))).replace(/=+$/,'')}`;
        const abbreviation = participantName
          .split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase();
        const newConversation: Conversation = {
          id: convId,
          type: 'direct',
          name: participantName,
          participants: [user.id, participantId],
          unreadCount: 0,
          avatar: abbreviation,
          createdAt: new Date(),
        };
        setMessageState(prev => ({
          ...prev,
          conversations: [newConversation, ...prev.conversations.filter(c => c.id !== convId)],
          messages: { ...prev.messages, [convId]: prev.messages[convId] || [] },
        }));
        return convId;
      }

      try {
        // Secure RPC handles idempotency + RLS server-side
        const { data: rpcData, error: rpcError } = await supabase.rpc(
          'create_direct_conversation' as any,
          {
            p_participant_id: participantId,
            p_participant_name: participantName,
          }
        );

        if (rpcError || !rpcData) {
          console.error('createDirectConversation RPC error:', rpcError);
          return '';
        }

        const convId = rpcData as string;
        const abbreviation = participantName
          .split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase();

        const newConversation: Conversation = {
          id: convId,
          type: 'direct',
          name: participantName,
          participants: [user.id, participantId],
          unreadCount: 0,
          avatar: abbreviation,
          createdAt: new Date(),
        };
        setMessageState(prev => ({
          ...prev,
          conversations: [newConversation, ...prev.conversations.filter(c => c.id !== convId)],
          messages: { ...prev.messages, [convId]: prev.messages[convId] || [] },
        }));
        return convId;
      } catch (e) {
        console.error('createDirectConversation: unexpected error', e);
        return '';
      }
    },
    [offlineMode, supabase, user]
  );

  const setActiveConversation = useCallback((conversationId: string | null) => {
    setMessageState(prev => ({ ...prev, activeConversationId: conversationId }));
    if (conversationId) {
      // In offline/mock mode, do not hit Supabase
      if (offlineMode) return;
      // Fetch recent messages if we have none
      const hasAny = (messageState.messages[conversationId] || []).length > 0;
      if (!hasAny) fetchRecentMessages(conversationId);
    }
  }, [messageState.messages, fetchRecentMessages, offlineMode]);

  const markAsRead = useCallback(
    async (conversationId: string) => {
      if (!user) return;
      if (offlineMode) {
        setMessageState(prev => ({
          ...prev,
          conversations: prev.conversations.map(conv =>
            conv.id === conversationId ? { ...conv, unreadCount: 0, lastReadAt: new Date() } : conv
          ),
          messages: { ...prev.messages, [conversationId]: (prev.messages[conversationId] || []).map(msg => ({ ...msg, isRead: true })) },
        }));
        return;
      }
      const now = new Date().toISOString();
      await supabase
        .from('conversation_participants')
        .update({ last_read_at: now })
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id);

      setMessageState(prev => {
        const updatedConversations = prev.conversations.map(conv =>
          conv.id === conversationId ? { ...conv, unreadCount: 0, lastReadAt: new Date(now) } : conv
        );
        const updatedMessages = {
          ...prev.messages,
          [conversationId]: prev.messages[conversationId]?.map(msg => ({ ...msg, isRead: true })) || [],
        };
        return { ...prev, conversations: updatedConversations, messages: updatedMessages };
      });
    },
    [offlineMode, supabase, user]
  );

  const getConversationMessages = useCallback(
    (conversationId: string): Message[] => messageState.messages[conversationId] || [],
    [messageState.messages]
  );

  const refreshMessages = useCallback(() => {
    loadData();
  }, [loadData]);

  const hasMore = useCallback((conversationId: string) => {
    return Boolean(messageState.moreAvailable?.[conversationId]);
  }, [messageState.moreAvailable]);

  const loadEarlierMessages = useCallback(
    async (conversationId: string): Promise<{ loaded: number; hasMore: boolean }> => {
      if (!user || offlineMode) return { loaded: 0, hasMore: false };
      const current = messageState.messages[conversationId] || [];
      const earliest = current[0]?.timestamp;

      let query = supabase
        .from('messages')
        .select('id,conversation_id,sender_id,content,created_at')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (earliest) {
        query = query.lt('created_at', earliest.toISOString());
      }

      const { data, error } = await query;
      if (error) {
        console.error('Error loading earlier messages:', error);
        return { loaded: 0, hasMore: hasMore(conversationId) };
      }
      const rows = data || [];

      if (!rows.length) {
        setMessageState(prev => ({
          ...prev,
          moreAvailable: { ...(prev.moreAvailable || {}), [conversationId]: false },
        }));
        return { loaded: 0, hasMore: false };
      }

      // Load sender names for the batch
      const senderIds = Array.from(new Set(rows.map(r => r.sender_id)));
      const senderMap = new Map<string, string>();
      if (senderIds.length) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id,first_name,last_name')
          .in('user_id', senderIds);
        (profiles || []).forEach(p => {
          const parts = [p.first_name, p.last_name].filter(Boolean);
          senderMap.set(p.user_id, parts.join(' ').trim() || 'Unknown');
        });
      }

      // Convert and prepend in ascending order
      const batch = rows
        .slice()
        .reverse()
        .map((m: any) => ({
          id: m.id,
          senderId: m.sender_id,
          senderName: senderMap.get(m.sender_id) || 'Unknown',
          content: m.content,
          timestamp: new Date(m.created_at),
          type: (messageState.conversations.find(c => c.id === conversationId)?.type) || 'direct',
          conversationId,
          isRead: true,
        } as Message));

      setMessageState(prev => ({
        ...prev,
        messages: {
          ...prev.messages,
          [conversationId]: [...batch, ...(prev.messages[conversationId] || [])],
        },
        moreAvailable: {
          ...(prev.moreAvailable || {}),
          [conversationId]: rows.length === 50,
        },
      }));

      return { loaded: batch.length, hasMore: rows.length === 50 };
    },
    [hasMore, messageState.conversations, messageState.messages, offlineMode, supabase, user]
  );

  const contextValue: MessageContextType = {
    ...messageState,
    sendMessage,
    sendAdminMessage,
    createDirectConversation,
    setActiveConversation,
    markAsRead,
    getConversationMessages,
    refreshMessages,
    loadEarlierMessages,
    hasMore,
    retryMessage: async (conversationId: string, messageId: string) => {
      const failed = (messageState.messages[conversationId] || []).find(m => m.id === messageId && m.failed);
      if (!failed) return;
      // remove the failed one and re-send optimistically
      setMessageState(prev => ({
        ...prev,
        messages: {
          ...prev.messages,
          [conversationId]: (prev.messages[conversationId] || []).filter(m => m.id !== messageId),
        },
      }));
      await sendMessage(conversationId, failed.content);
    },
  };

  return <MessageContext.Provider value={contextValue}>{children}</MessageContext.Provider>;
}
